import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Box, Typography, Paper, Fade, Alert, Switch, FormControlLabel
} from '@mui/material';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import KeyboardAltRoundedIcon from '@mui/icons-material/KeyboardAltRounded';

import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

import { loginUser } from '../services/authServices/loginService';
import { fetchDailyPlan } from '../services/authServices/getSetionService';
import { CenterContent, VList } from '../utils/layouts';
import Loader from '../utils/Loader';
import ManualLoginForm from './ManualLoginForm'; 

const ENV_USER_ID = process.env.REACT_APP_LOGIN_USER_ID;
const ENV_PASSWORD = process.env.REACT_APP_LOGIN_PASSWORD;

const LoginPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authLoading = useSelector((state) => state.auth?.loading);
  const sessionLoading = useSelector((state) => state.session?.loading);
  const isLoading = authLoading || sessionLoading;

  const [localError, setLocalError] = useState(null);
  const [isManualLogin, setIsManualLogin] = useState(false);

  const html5QrCode = useRef(null);
  const isProcessing = useRef(false);

  const handleErrorResponse = (err) => {
    let extractedMessage = err?.response?.data?.message || err?.message || err;
    let finalErrorMessage = typeof extractedMessage === 'string' ? extractedMessage : "حدث خطأ غير متوقع";

    if (finalErrorMessage.includes("No plan found") || finalErrorMessage.includes("404")) {
      finalErrorMessage = "لم يتم العثور على بيانات مطابقة. تأكد من صحة المدخلات.";
    }

    setLocalError(finalErrorMessage);
  };

  const processQrLogin = async (qrContent) => {
    if (isProcessing.current) return;
    isProcessing.current = true;

    setLocalError(null);
    try {
      const sessionHash = qrContent;
      if (!sessionHash) throw new Error("رمز QR غير صالح أو فارغ");

      await dispatch(loginUser({ 
        userId: ENV_USER_ID, 
        password: ENV_PASSWORD 
      })).unwrap();

      await dispatch(fetchDailyPlan(sessionHash)).unwrap();

      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('sessionHash', sessionHash);

      setIsAuthenticated(true);
      navigate('/splash', { replace: true });

    } catch (err) {
      handleErrorResponse(err);
      setTimeout(() => { isProcessing.current = false; }, 2000);
    }
  };

  useEffect(() => {
    let isMounted = true;
    let scannerRef = null;

    const startScanner = async () => {
      try {
        const qrBox = document.getElementById("qr-reader");
        if (qrBox && qrBox.innerHTML !== "") return;

        scannerRef = new Html5Qrcode("qr-reader");
        html5QrCode.current = scannerRef;

        const config = {
          fps: 15,
          qrbox: { width: 280, height: 280 },
          useBarCodeDetectorIfSupported: true,
          formatsToSupport: [ Html5QrcodeSupportedFormats.QR_CODE ],
          videoConstraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 },
            advanced: [{ focusMode: "continuous" }]
          }
        };

        if (isMounted) {
          await scannerRef.start(
            { facingMode: "environment" },
            config,
            (text) => {
              if (!isProcessing.current) {
                if (html5QrCode.current) {
                  html5QrCode.current.stop().catch(console.error);
                }
                processQrLogin(text);
              }
            },
            () => {} 
          );
        }
      } catch (err) {
        if (isMounted) setLocalError("تعذر تشغيل الكاميرا. يرجى التحقق من الأذونات.");
      }
    };

    setTimeout(() => {
      if (isMounted && !isManualLogin) {
        startScanner();
      }
    }, 100);

    return () => {
      isMounted = false;
      if (scannerRef && scannerRef.isScanning) {
        scannerRef.stop().then(() => {
          scannerRef.clear();
        }).catch(console.error);
      }
    };
  }, [isManualLogin]);

  return (
    <CenterContent minHeight="100vh">
      <Fade in={true} timeout={800}>
        <Paper variant='section' sx={{minWidth:'300px',minHeight:'200px'}}>
          <VList>
            <Typography variant='pageTitle' textAlign={'center'}>
              {isManualLogin ? "تسجيل الدخول اليدوي" : "تسجيل الدخول بالـ QR"}
            </Typography>
            
            {localError && <Alert severity="error" sx={{ width: '100%', mb: 1 }}>{localError}</Alert>}

            <Box sx={{ width: '100%', display: isManualLogin ? 'none' : 'block' }}>
              <Typography variant='hint' sx={{ mb: 2, textAlign: 'center', display: 'block' }}>
                وجه الكاميرا نحو رمز الاستجابة السريع الخاص بالجلسة
              </Typography>
              <Box id="qr-reader" sx={{ width: '100%', borderRadius: "10px", overflow: 'hidden', backgroundColor: '#000' }} />
            </Box>

            {/* ✅ تم التعديل هنا: تمرير setIsAuthenticated للفورمة اليدوية */}
            {isManualLogin && (
              <ManualLoginForm 
                setIsAuthenticated={setIsAuthenticated} 
              />
            )}

            {isLoading && !isManualLogin && (
              <Loader text="جاري التحقق من الجلسة..." size={30} sx={{ mt: 2 }} />
            )}

            <FormControlLabel
              control={
                <Switch 
                  checked={isManualLogin} 
                  onChange={(e) => {
                    setIsManualLogin(e.target.checked);
                    setLocalError(null); 
                  }} 
                  color="primary" 
                  disabled={isLoading}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isManualLogin ? <QrCodeScannerRoundedIcon fontSize="small" /> : <KeyboardAltRoundedIcon fontSize="small" />}
                  {isManualLogin ? "استخدام ماسح الـ QR" : "إدخال يدوي"}
                </Typography>
              }
              sx={{ mt: 3, alignSelf: 'center' }}
            />
          </VList>
        </Paper>
      </Fade>
    </CenterContent>
  );
};

export default LoginPage;